import { Mesh, Program, Texture, Text, Geometry, Color, Vec3 } from "ogl";
import { rgb2hex } from "@utils/colors";

import neumaticCompressedRegularJson from "@assets/font/NeumaticCompressed-Regular.json";
import neumaticCompressedRegularImageSource from "@assets/font/NeumaticCompressed-Regular.png";
import homageCondensedBlackJson from "@assets/font/HomageCondensed-Black.json";
import homageCondensedBlackImageSource from "@assets/font/HomageCondensed-Black.png";

import fragment100 from "./shaders/msdfFragment100.glsl";
import fragment300 from "./shaders/msdfFragment300.glsl";
import vertex100 from "./shaders/msdfVertex100.glsl";
import vertex300 from "./shaders/msdfVertex300.glsl";

class HugeText {
  constructor({
    node,
    gl,
    renderer,
    scene,
    screen,
    viewport,
    index,
    preloadedResources,
  }) {
    this.node = node;
    this.textContent = this.node.textContent;

    const dataFont = this.node.dataset.hugeTextFont ?? "neumatic";
    switch (dataFont) {
      case "homage":
        this.font = {
          json: homageCondensedBlackJson,
          image: preloadedResources[homageCondensedBlackImageSource] ?? null,
          imageSource: homageCondensedBlackImageSource,
        };
        break;

      case "neumatic":
      default:
        this.font = {
          json: neumaticCompressedRegularJson,
          image:
            preloadedResources[neumaticCompressedRegularImageSource] ?? null,
          imageSource: neumaticCompressedRegularImageSource,
        };
        break;
    }
    let textColor = getComputedStyle(this.node).getPropertyValue("color");
    textColor = rgb2hex(textColor);
    this.color = new Color(textColor ?? "#FFFFFF");
    this.z = parseFloat(this.node.dataset.hugeTextZ ?? "0");
    this.renderOrder = parseInt(this.node.dataset.hugeTextRenderOrder ?? "0");
    this.deptTest = this.node.dataset.hugeTextDepthTest !== "false";

    // check if isParallaxEnabled is set
    this.isParallaxEnabled =
      this.node.dataset.hugeTextParallax !== undefined &&
      this.node.dataset.hugeTextParallax !== "false";

    this.gl = gl;
    this.renderer = renderer;
    this.scene = scene;
    this.screen = screen;
    this.viewport = viewport;
    this.x = window.scrollX;
    this.index = index;
    this.needsUpdatedBounds = false;

    this.createMesh();
    this.initialBounds = this.node.getBoundingClientRect();
    this.createBounds();

    this.onResize();
  }

  createMesh = () => {
    const texture = new Texture(this.gl, {
      generateMipmaps: false,
    });

    const program = new Program(this.gl, {
      // Get fallback shader for WebGL1 - needed for OES_standard_derivatives ext
      vertex: this.renderer.isWebgl2 ? vertex300 : vertex100,
      fragment: this.renderer.isWebgl2 ? fragment300 : fragment100,
      uniforms: {
        tMap: { value: texture },
        uViewportSizes: { value: [this.viewport.width, this.viewport.height] },
        uStrength: { value: 0 },
        uColor: { value: this.color },
      },
      transparent: true,
      cullFace: null,
      depthWrite: false,
      depthTest: this.deptTest,
    });

    if (!this.font.image) {
      this.font.image = new Image();
      this.font.image.crossOrigin = "anonymous";
      this.font.image.src = this.font.imageSource;
    }

    this.font.image.addEventListener("load", (_) => {
      texture.image = this.font.image;
      // console.log(
      //   "%c Media texture ",
      //   "background: cyan; color: black",
      //   "loaded from network",
      //   this.font.image.src
      // );
    });

    // get it from cache if it is already loaded
    if (this.font.image.complete) {
      texture.image = this.font.image;
      // console.log(
      //   "%c Media texture ",
      //   "background: cyan; color: black",
      //   "loaded from cache",
      //   this.font.image.src
      // );
    }

    this.textSpecs = new Text({
      font: this.font.json,
      text: this.textContent.toUpperCase(),
      width: 1,
      letterSpacing: 0,
      size: 0.1,
      lineHeight: 1,
    });

    // Pass the generated buffers into a geometry
    const geometry = new Geometry(this.gl, {
      position: { size: 3, data: this.textSpecs.buffers.position },
      uv: { size: 2, data: this.textSpecs.buffers.uv },
      // id provides a per-character index, for effects that may require it
      id: { size: 1, data: this.textSpecs.buffers.id },
      index: { data: this.textSpecs.buffers.index },
    });

    this.mesh = new Mesh(this.gl, { geometry, program });
    this.mesh.position.z = this.z;
    this.mesh.renderOrder = this.renderOrder;
    this.mesh.setParent(this.scene);

    // this.helperMesh = new WireMesh(this.gl, {
    //   geometry: new Plane(this.gl, {
    //     width: 1,
    //     height: 1,
    //   }),
    //   wireColor: new Vec3(1, 0, 0),
    // });
    // this.helperMesh.setParent(this.scene);
  };

  createBounds = () => {
    this.bounds = this.node.getBoundingClientRect();
    this.offsetX = this.x;

    this.updateScale();
  };

  updateScale = () => {
    this.finalTextWidthInScene =
      (this.viewport.width / this.screen.width) * this.bounds.width;
    const scaleToMatchFinalTextWidth =
      this.finalTextWidthInScene / this.textSpecs.width;

    this.mesh.scale.y = scaleToMatchFinalTextWidth;
    this.mesh.scale.x = scaleToMatchFinalTextWidth;

    // this.helperMesh.scale.x =
    //   (this.viewport.width * this.bounds.width) / this.screen.width;
    // this.helperMesh.scale.y =
    //   (this.viewport.height * this.bounds.height) / this.screen.height;
  };

  updateX = (x = 0) => {
    // NOTE: make parallaxOffset 0 if you don't want parallax
    const parallaxOffset = this.isParallaxEnabled
      ? Math.min(
          Math.max(
            this.bounds.left +
              this.bounds.width / 2 -
              (x - this.offsetX + this.screen.width / 2),
            -this.bounds.width / 2
          ),
          this.bounds.width / 2
        )
      : 0;

    this.mesh.position.x =
      -(this.viewport.width / 2) +
      ((this.bounds.left - (x - this.offsetX - parallaxOffset)) /
        this.screen.width) *
        this.viewport.width;

    // this.helperMesh.position.x =
    //   -(this.viewport.width / 2) +
    //   this.helperMesh.scale.x / 2 +
    //   ((this.bounds.left - (x - this.offsetX)) / this.screen.width) *
    //     this.viewport.width;
  };

  updateY = (y = 0) => {
    this.mesh.position.y =
      this.viewport.height / 2 -
      ((this.bounds.top - y) / this.screen.height) * this.viewport.height +
      (this.textSpecs.height / this.textSpecs.width) *
        this.finalTextWidthInScene *
        0.035;

    // this.helperMesh.position.y =
    //   this.viewport.height / 2 -
    //   this.helperMesh.scale.y / 2 -
    //   ((this.bounds.top - y) / this.screen.height) * this.viewport.height;
  };

  update = (scroll) => {
    this.x = scroll.x;

    if (scroll.isChanged && this.needsUpdatedBounds) {
      this.createBounds();

      this.needsUpdatedBounds = false;
    }

    this.updateX(scroll.x);
    this.updateY();

    this.mesh.program.uniforms.uStrength.value =
      ((scroll.current - scroll.last) / this.screen.height) * 5;
  };

  onResize = (sizes) => {
    if (sizes) {
      const { screen, viewport } = sizes;

      if (screen) this.screen = screen;
      if (viewport) {
        this.viewport = viewport;

        this.mesh.program.uniforms.uViewportSizes.value = [
          this.viewport.width,
          this.viewport.height,
        ];
      }
    }

    this.createBounds();
    this.needsUpdatedBounds = true;
  };
}

export default HugeText;
